import { DataGrid } from 'src/feature/data-grid/data-grid';
import { useDataGridSearchParams } from 'src/feature/data-grid/use-data-grid-search-params/use-data-grid-search-params';
import {
  DEFAULT_DATA_GRID_MODEL,
  DEFAULT_PAGE_SIZE,
} from 'src/config/data-grid.config';
import { useTranslation } from 'react-i18next';
import { Snackbar } from 'src/shared/snackbar/snackbar';

import { useGetInvoicesPaginatedQuery } from '@service/invoice-v1.service';
import { useCallback, useState } from 'react';
import { DataGridQueryModel } from '@model/data-grid.model';
import { SortOrder } from '@delmonicos/shared-sdk/libs/emsp/src/libs/core/src/utils/mongo/pagination/models/pagination.model';
import { GetInvoicesPaginatedModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/shared/src/invoice-service/models/input/get-invoices-paginated.model';
import { useInvoiceFilter } from './use-invoice-filter';
import { useInvoiceColumn } from './use-invoice-column';

export function InvoiceDataGrid(): React.JSX.Element {
  const { t } = useTranslation();
  const columns = useInvoiceColumn();

  // PAGINATION & FILTERS //////////////////////////////////////////////////////
  const filterDefinition = useInvoiceFilter();

  const { model: urlModel } = useDataGridSearchParams(DEFAULT_DATA_GRID_MODEL);
  const [model, setModel] = useState<DataGridQueryModel>(urlModel);

  const [params, setParams] = useState<GetInvoicesPaginatedModel>(() => {
    const pageSize =
      DEFAULT_DATA_GRID_MODEL.paginate?.pageSize ?? DEFAULT_PAGE_SIZE;
    const page = DEFAULT_DATA_GRID_MODEL.paginate?.page ?? 0;

    return {
      sortField: 'createdAt',
      sortOrder: SortOrder.DESC,
      textToSearch: urlModel.textSearch || undefined,
      emspUserDid: urlModel.filter?.pseudo?.toString() || undefined,
      limit: pageSize,
      offset: page * pageSize,
    };
  });

  const onModelChange = useCallback(
    (newModel: DataGridQueryModel) => {
      setModel(newModel);

      setParams((prev) => {
        const pageSize =
          newModel.paginate?.pageSize ?? prev.limit ?? DEFAULT_PAGE_SIZE;
        const page = newModel.paginate?.page ?? 0;

        return {
          ...prev,
          limit: pageSize,
          offset: page * pageSize,
          textToSearch: newModel.textSearch || undefined,
        };
      });
    },

    [setModel],
  );

  const {
    data: paginatedData,
    isFetching,
    isError,
  } = useGetInvoicesPaginatedQuery(params);

  return (
    <>
      <DataGrid
        columns={columns}
        filterDefinitionModel={filterDefinition}
        loading={isFetching}
        rows={paginatedData?.docs ?? []}
        rowCount={paginatedData?.totalDocs ?? 0}
        model={model}
        onModelChange={onModelChange}
        initialState={{
          columns: {
            columnVisibilityModel: {
              quoteId: false,
              orderId: false,
              sessionId: false,
            },
          },
        }}
      />

      {isError && (
        <Snackbar
          variant="error"
          message={t('error.get-list-invoice-v1-query')}
        />
      )}
    </>
  );
}
